import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import {
  Database,
  HardDrive,
  RefreshCw,
  Users,
  Package,
  Settings,
  Server,
  Palette,
  Puzzle,
  MessageSquare,
  Shield,
  FileCode,
  Rss,
  Activity,
} from "lucide-react"

const resourceIcons = {
  users: Users,
  configs: FileCode,
  purchase_plans: Package,
  settings: Settings,
  vpn_panels: Server,
  themes: Palette,
  plugins: Puzzle,
  ui_messages: MessageSquare,
  feature_toggles: Activity,
  subscription_sources: Rss,
  clean_ips: HardDrive,
  channels: Database,
  secrets: Shield,
}

const resourceLabels = {
  users: "Users",
  configs: "Configs",
  purchase_plans: "Plans",
  settings: "Settings",
  vpn_panels: "Panels",
  themes: "Themes",
  plugins: "Plugins",
  ui_messages: "Messages",
  feature_toggles: "Features",
  subscription_sources: "Subscriptions",
  clean_ips: "Clean IPs",
  channels: "Channels",
  secrets: "Secrets",
}

export default function ResourcesPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["resources-stats"],
    queryFn: () => api.get("/admin/stats"),
  })

  const stats = data?.data || {}
  const resourceEntries = Object.entries(stats).filter(
    ([key]) => resourceLabels[key]
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Resource Explorer</h1>
          <p className="text-muted-foreground">Browse and manage all system resources</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-muted rounded" />
                <div className="h-4 w-4 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {resourceEntries.map(([key, count]) => {
            const Icon = resourceIcons[key] || Database
            return (
              <Card key={key} className="cursor-pointer hover:border-primary transition-colors">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {resourceLabels[key]}
                  </CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{count}</div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {!isLoading && resourceEntries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No resource data available</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
